import { useEffect } from 'react';
import useWebSocket from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { replace } from 'redux-first-history';
import { useToast } from '@chakra-ui/react';

import type {
  LeadUpdateArgs,
  PhotoUpdateArgs,
  BroadcoastToWebSocketClientsArgs,
  LeadAuthenticatedArgs,
  SetConnectionIdArgs,
} from '@backend/src/lib/websockets';
import { API_WEBSOCKET_ROOT } from 'utils';
import logger from 'logger';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { removeUpload } from 'slices/uploadsSlice';
import {
  handleWebsocketLeadUpdate,
  savePhotoToStore,
  saveToStore,
} from 'actions/leadActions';
import { setWebSocketConnectionId } from 'actions/appActions';
import { verifyCloseLocations } from 'actions/locationActions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useWebsocket = (): WebSocketHook<unknown, MessageEvent<any> | null> => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const activeLeadID = useAppSelector((state) => state.app?.activeLeadID);
  const token = useAppSelector((state) => state.app.token as string);

  const s = useWebSocket(
    activeLeadID ? `${API_WEBSOCKET_ROOT}?_id=${activeLeadID}` : null,
    {
      onOpen: async () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection established.');

        s.sendJsonMessage({
          action: 'auth',
          data: { _id: activeLeadID, token },
        });
      },
      onMessage: async (message) => {
        const { event, data } = JSON.parse(message.data) as Omit<
          BroadcoastToWebSocketClientsArgs,
          'leadId'
        >;
        logger.info(`WebSocket onMessage: ${event}`, { event, data });

        switch (event) {
          case 'SetConnectionId':
            dispatch(
              setWebSocketConnectionId(
                (data as SetConnectionIdArgs['data']).connectionId,
              ),
            );
            break;

          case 'LeadAuthenticated':
            dispatch(
              saveToStore((data as LeadAuthenticatedArgs['data']).updatedLead),
            );
            dispatch(
              verifyCloseLocations(
                (data as LeadAuthenticatedArgs['data']).updatedLead.fromAddress,
              ),
            );
            break;

          case 'LeadAuthExpired':
            dispatch(replace('/reauth'));
            break;

          case 'LeadUpdate':
            dispatch(
              handleWebsocketLeadUpdate({
                partialLead: (data as LeadUpdateArgs['data']).updatedLead,
                connectionId: data.connectionId,
              }),
            );
            break;

          case 'PhotoUpdate':
            dispatch(
              savePhotoToStore((data as PhotoUpdateArgs['data']).updatedPhoto),
            );
            break;

          default:
            break;
        }

        if (data.removePhotoUploadId) {
          dispatch(removeUpload(data.removePhotoUploadId));
        }
        if (data.notification) {
          const id = data.notification.id || undefined;

          if (id && toast.isActive(id)) {
            toast.update(id, {
              title: data.notification.title,
              description: data.notification.description,
              status: data.notification.status || 'success',
            });
          } else {
            toast({
              id,
              title: data.notification.title,
              description: data.notification.description,
              status: data.notification.status || 'success',
            });
          }
        }
      },
      onClose: () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection closed.');
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket connection error.', { error });
      },
      onReconnectStop: () => {
        // eslint-disable-next-line no-console
        console.info('WebSocket connection onReconnectStop.');
      },
      share: true,
      filter: () => false,
      retryOnError: true,
      shouldReconnect: () => true,
    },
  );

  useEffect(() => {
    if (activeLeadID && token) {
      s.sendJsonMessage({
        action: 'auth',
        data: { _id: activeLeadID, token },
      });
    }
    // For some reason, this wants me to add "s" but that puts this into an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeadID, token]);

  return s;
};

export default useWebsocket;
